import { EditNameModalRef } from 'basic-ui-lib/components/Modal/EditNameModal';
import { useRef } from 'react';
import { useModalConfirmRef } from 'widgets/ModalConfirm';
import { useDeleteMediaItem } from './useDeleteMediaItem';
import { IcCopy, IcEdit, IcMoveStore, IcTrash } from 'basic-ui-lib';
import { IMediaItem, MediaType } from 'modules/media/interfaces/api';
import mediaApis from '../apis';
import toast from 'utils/toast';
import { MoveToStoresModalRef } from 'modules/media/screens/Dashboard/LibraryList/MoveToStoreModal';
import { MoveToFolderModalRef } from 'modules/media/screens/Dashboard/LibraryList/MoveToFolderModal';

interface UseMediaMoreActionProps {
  currentPath: string;
  onSuccess: (currentPath: string) => void;
  deleteSuccess?: () => void;
  storeId?: string;
}

export const useMediaMoreAction = ({
  currentPath,
  onSuccess,
  deleteSuccess,
  storeId,
}: UseMediaMoreActionProps) => {
  const modalConfirmRef = useModalConfirmRef();
  const editNameModalRef = useRef<EditNameModalRef>(null);
  const moveToStoresModalRef = useRef<MoveToStoresModalRef>(null);
  const moveToFolderModalRef = useRef<MoveToFolderModalRef>(null);

  const { deleteItem } = useDeleteMediaItem({
    currentPath,
    onSuccess,
    deleteSuccess,
  });

  const getListMenu = (record: IMediaItem) => [
    {
      key: 'edit_name',
      title: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
    },
    ...(record.type !== MediaType.FOLDER
      ? [
          {
            key: 'copy_link',
            title: 'Copy Link',
            icon: <IcCopy width={16} height={16} />,
          },
        ]
      : []),
    ...(storeId
      ? [
          {
            key: 'move_to_folder',
            title: 'Move to Folder',
            icon: <IcMoveStore />,
          },
        ]
      : [
          {
            key: 'move_to_store',
            title: 'Move to Store',
            icon: <IcMoveStore />,
          },
        ]),
    {
      key: 'delete_media',
      title: 'Delete',
      icon: <IcTrash width={16} height={16} />,
    },
  ];

  const handleDelete = (item: IMediaItem) => {
    modalConfirmRef.current?.open(() => deleteItem(item), {
      message: `Do you want to delete this ${item.type.toLowerCase()}?`,
    });
  };

  const handleEditName = async (id: string, newName: string) => {
    await mediaApis.renameMediaItem(id, newName);
    toast.success('Name updated successfully');
    onSuccess(currentPath);
  };

  const handleMenuClick = (menuItem: any, record: IMediaItem) => {
    switch (menuItem.key) {
      case 'edit_name':
        editNameModalRef.current?.open({
          currentName: record.name,
          id: record.id,
          onFinish: handleEditName,
        });
        break;
      case 'copy_link':
        navigator.clipboard.writeText(record.url || '');
        toast.success('Link copied to clipboard');
        break;
      case 'delete_media':
        handleDelete(record);
        break;
      case 'move_to_folder':
        moveToFolderModalRef.current?.open(record);
        break;
      case 'move_to_store':
        moveToStoresModalRef.current?.open(record);
        break;
      default:
        break;
    }
  };

  return {
    getListMenu,
    handleMenuClick,
    modalConfirmRef,
    editNameModalRef,
    moveToStoresModalRef,
    moveToFolderModalRef,
  };
};
